import axios from 'axios';
import { AuthResponse, LoginRequest, RegisterRequest } from '../types/auth';
import type { SubjectId, SubjectsPayload } from '../subjects/types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://crackedat.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
});

const APP_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

export const SUBJECT_HEADER = 'X-Subject';

export const setActiveSubjectHeader = (subjectId: SubjectId) => {
  api.defaults.headers.common[SUBJECT_HEADER] = subjectId;
};

const buildQueryString = (params?: Record<string, any>): string => {
  if (!params) {
    return '';
  }

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.append(key, String(value));
  });

  return searchParams.toString();
};

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't automatically redirect - let the components handle auth failures
    return Promise.reject(error);
  }
);

export interface FreeTierUsageSummary {
  date: string;
  practice_questions_used: number;
  practice_questions_limit: number;
  survival_sessions_used: number;
  survival_sessions_limit: number;
  rush_sessions_used: number;
  rush_sessions_limit: number;
  resets_on: string;
  resets_at: string;
}

export type PassId = 'year12' | 'year11_12' | 'lifetime';

export interface AccessPass {
  id: PassId;
  name: string;
  price_cents: number;
  price_display: string;
  expires_at: string | null;
  description: string;
}

export interface Pricing {
  currency: string;
  passes: AccessPass[];
}

export interface SubscriptionStatus {
  has_active_subscription: boolean;
  is_on_free_plan: boolean;
  access: 'active' | 'free' | 'inactive';
  plan: 'monthly' | 'annual' | PassId | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  billing_management: 'web' | 'app_store' | null;
  free_tier_usage: FreeTierUsageSummary | null;
}

export interface AdminHealthPayload {
  status: 'healthy' | 'degraded' | 'unhealthy';
  checked_at: string;
  cache_ttl_seconds: number;
  application: {
    name: string;
    environment: string;
    version: string;
    git_commit?: string | null;
  };
  database: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    reachable: boolean;
    latency_ms: number;
    dialect?: string;
    active_sessions?: number;
    idle_in_transaction_sessions?: number;
    lock_waiters?: number;
    longest_query_seconds?: number;
    error?: string;
  };
  redis: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    reachable: boolean;
    latency_ms: number;
    error?: string;
  };
  system: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    load_average: { '1m': number; '5m': number; '15m': number };
    memory: { total_mb: number | null; available_mb: number | null; used_percent: number | null };
    disk: { total_gb: number; free_gb: number; used_percent: number };
    uptime_seconds: number | null;
  };
}

export interface ReviewTag {
  id: string;
  name: string;
}

export interface ReviewQuestionPayload {
  id: string;
  year: number;
  exam_type: number;
  question_number: number;
  question_type: string;
  vce_area_of_study: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  option_e?: string | null;
  correct_answer: string;
  difficulty_rating: number | null;
  worked_solution?: string | null;
  tags?: ReviewTag[];
}

export interface GameSessionReviewQuestion {
  attempt_id: string;
  question_number: number;
  question: ReviewQuestionPayload;
  selected_answer?: string | null;
  is_correct: boolean;
  correct_answer: string;
  time_spent_seconds?: number | null;
}

export interface GameSessionReview {
  session_id: string;
  game_mode: string;
  score: number;
  questions_answered: number;
  correct_answers: number;
  accuracy_percentage: number;
  duration_minutes?: number | null;
  completed_at?: string | null;
  questions: GameSessionReviewQuestion[];
}

export interface TagWithCount {
  id: string;
  name: string;
  question_count: number;
  area_of_study: string;
}

export interface SimilarQuestionResponse {
  question_text: string;
  solution: string;
  worked_solution: string;
  options?: Record<string, string | null> | null;
  correct_answer?: string | null;
}

export type AITutorGenerationStatus = 'pending' | 'passed' | 'failed' | 'partial' | 'regenerated';

export interface AITutorCurriculumNode {
  id?: string | null;
  area_of_study?: string | null;
  topic?: string | null;
  subtopic?: string | null;
}

export interface AITutorGeneratedQuestion {
  id: string;
  position: number;
  source_question_id: string;
  question_type: 'MC' | 'SA' | 'ER';
  question_latex: string;
  options: Partial<Record<'A' | 'B' | 'C' | 'D', string>>;
  correct_answer: string;
  solution_latex: string;
  marking_guide_latex?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface AITutorGenerateResponse {
  set_id: string;
  curriculum_node?: AITutorCurriculumNode | null;
  generated_questions: AITutorGeneratedQuestion[];
  status: AITutorGenerationStatus;
  status_detail: string;
  error_code?: string | null;
}

export interface BookmarkResponse {
  question_id: string;
  is_bookmarked: boolean;
}

export type TagDirectoryResponse = Record<string, TagWithCount[]>;

export interface TagFilterCountsResponse {
  selected_count: number;
  counts_by_tag_id: Record<string, number>;
}

export const authAPI = {
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  logout: async () => {
    await api.post('/auth/logout');
  },

  googleAuth: () => {
    window.location.href = `${APP_BASE_URL}/auth/google`;
  },
};

export const aiTutorAPI = {
  generateQuestions: async (prompt: string): Promise<AITutorGenerateResponse> => {
    const response = await api.post<AITutorGenerateResponse>('/ai-tutor/generate', {
      prompt,
      include_solution: true,
    });
    return response.data;
  },

  getGeneratedSet: async (setId: string): Promise<AITutorGenerateResponse> => {
    const response = await api.get<AITutorGenerateResponse>(`/ai-tutor/generated-sets/${setId}`);
    return response.data;
  },
};

export const subjectAPI = {
  list: async (): Promise<SubjectsPayload> => (await api.get('/subjects')).data,
  add: async (subjectId: SubjectId): Promise<SubjectsPayload> =>
    (await api.put(`/subjects/${subjectId}`, {})).data,
  update: async (subjectId: SubjectId, body: { hidden?: boolean; opened?: boolean }): Promise<SubjectsPayload> =>
    (await api.patch(`/subjects/${subjectId}`, body)).data,
};

export const questionAPI = {
  getQuestions: async (params?: any) => {
    const response = await api.get('/questions/', {
      params,
      paramsSerializer: (requestParams) => buildQueryString(requestParams),
    });
    return response.data;
  },

  getRandomQuestion: async (params?: any) => {
    const response = await api.get('/questions/random/practice', {
      params,
      paramsSerializer: (requestParams) => buildQueryString(requestParams),
    });
    return response.data;
  },

  getTagDirectory: async (): Promise<TagDirectoryResponse> => {
    const response = await api.get<TagDirectoryResponse>('/questions/tags/directory');
    return response.data;
  },

  getTagFilterCounts: async (params?: { question_type?: string | string[]; tags?: string[]; exclude_tags?: string[]; exclude_areas?: string[] }): Promise<TagFilterCountsResponse> => {
    const response = await api.get<TagFilterCountsResponse>('/questions/tags/counts', {
      params,
      paramsSerializer: (requestParams) => buildQueryString(requestParams),
    });
    return response.data;
  },

  getTopicStats: async () => {
    const response = await api.get('/questions/topics/stats');
    return response.data;
  },

  submitAnswer: async (answerData: {
    question_id: string;
    selected_answer: string;
    time_spent_seconds?: number;
    game_mode?: string;
    session_id?: string;
  }) => {
    const response = await api.post('/questions/submit-answer', answerData);
    return response.data;
  },

  getUserStats: async () => {
    const response = await api.get('/questions/user-stats');
    return response.data;
  },

  getAvailableYears: async (): Promise<number[]> => {
    const response = await api.get('/questions/years');
    return response.data;
  },

  getSurvivalQuestion: async (questionNumber: number) => {
    const response = await api.get(`/questions/survival/${questionNumber}`);
    return response.data;
  },

  getRushQuestion: async (questionNumber: number, durationMinutes: number) => {
    const response = await api.get(`/questions/rush/${questionNumber}`, {
      params: { duration_minutes: durationMinutes }
    });
    return response.data;
  },

  generateSimilarQuestion: async (questionId: string): Promise<SimilarQuestionResponse> => {
    const response = await api.post(`/questions/${questionId}/similar`);
    return response.data;
  },

  bookmarkQuestion: async (questionId: string): Promise<BookmarkResponse> => {
    const response = await api.post<BookmarkResponse>(`/questions/${questionId}/bookmark`);
    return response.data;
  },

  unbookmarkQuestion: async (questionId: string): Promise<BookmarkResponse> => {
    const response = await api.delete<BookmarkResponse>(`/questions/${questionId}/bookmark`);
    return response.data;
  },
};

export interface SkillDefinition {
  slug: string;
  name: string;
  description: string;
}

export interface GraphFeaturePoint {
  x: number;
  y: number;
  label: string;
  coordinate_label: string;
  kind?: string;
}

export interface GraphAsymptoteFeature {
  type: 'vertical' | 'horizontal';
  label: string;
  coordinate_label: string;
  x?: number;
  y?: number;
}

export interface GraphChoiceFeatures {
  x_intercepts: GraphFeaturePoint[];
  y_intercept: GraphFeaturePoint | null;
  turning_points: GraphFeaturePoint[];
  anchors: GraphFeaturePoint[];
  asymptotes: GraphAsymptoteFeature[];
}

export interface GraphTransformationDescriptor {
  name: string;
  description: string;
  parameters?: Record<string, unknown>;
}

export interface GraphProblemMetadata {
  function_type?: string;
  transformation_count_bucket?: '1' | '2' | '3' | '3+';
  transformation_count?: number;
  transformations?: GraphTransformationDescriptor[];
  catalog_question_id?: string;
}

export interface GraphChoice {
  label: string;
  function_latex: string;
  tikz_code: string;
  svg?: string;
  view_range?: {
    x_min: number;
    x_max: number;
    y_min: number;
    y_max: number;
  };
  features?: GraphChoiceFeatures;
  is_correct: boolean;
  distractor_reason?: string;
}

export interface SkillProblem {
  mode?: 'graph_choice' | string;
  question_id?: string;
  catalog_question_id?: string;
  problem_latex: string;
  solution_latex: string;
  explanation_latex?: string;
  choices?: GraphChoice[];
  marks?: number;
  metadata?: GraphProblemMetadata;
  function_type?: string;
  transformation_count_bucket?: '1' | '2' | '3' | '3+';
  transformation_count?: number;
  transformations?: GraphTransformationDescriptor[];
}

export interface GraphSketchingProgress {
  last_attempt_at: string | null;
  last_attempt_was_correct: boolean | null;
  last_correct_at: string | null;
  last_incorrect_at: string | null;
  completed_at: string | null;
}

export interface GraphSketchingAttemptResponse {
  question_id: string;
  is_correct: boolean;
  progress: GraphSketchingProgress;
}

export const skillAPI = {
  getSkills: async (): Promise<SkillDefinition[]> => {
    const response = await api.get('/skills');
    return response.data;
  },
  getSkillProblems: async (
    skillSlug: string,
    params?: Record<string, any>
  ): Promise<{ problems: SkillProblem[] }> => {
    const response = await api.get(`/skills/${skillSlug}/problems`, {
      params,
      paramsSerializer: (requestParams) => buildQueryString(requestParams),
    });
    return response.data;
  },
  recordGraphSketchingAnswer: async (payload: {
    question_id: string;
    selected_choice_label?: string;
    is_correct?: boolean;
  }): Promise<GraphSketchingAttemptResponse> => {
    const response = await api.post('/skills/graph_sketching/submit-answer', payload);
    return response.data;
  },
  downloadSkillWorksheetPdf: async (
    skillSlug: string,
    params?: Record<string, any>
  ): Promise<Blob> => {
    const response = await api.get(`/skills/${skillSlug}/worksheet/pdf`, {
      params,
      paramsSerializer: (requestParams) => buildQueryString(requestParams),
      responseType: 'blob',
    });
    return response.data;
  },
};

export const gameSessionAPI = {
  startSession: async (gameMode: string, durationMinutes?: number) => {
    const response = await api.post('/game-sessions/start', {
      game_mode: gameMode,
      duration_minutes: durationMinutes
    });
    return response.data;
  },

  updateProgress: async (sessionId: string, progress: {
    questions_answered: number;
    correct_answers: number;
    lives_remaining?: number;
    is_completed?: boolean;
  }) => {
    const response = await api.put(`/game-sessions/${sessionId}/progress`, progress);
    return response.data;
  },

  getPersonalBests: async () => {
    const response = await api.get('/game-sessions/personal-bests');
    return response.data;
  },

  getGameProgress: async (sessionId: string) => {
    const response = await api.get(`/game-sessions/${sessionId}/progress`);
    return response.data;
  },

  getRecentSessions: async (limit = 10) => {
    const response = await api.get('/game-sessions/recent', { params: { limit } });
    return response.data;
  },

  getSessionReview: async (sessionId: string): Promise<GameSessionReview> => {
    const response = await api.get<GameSessionReview>(`/game-sessions/${sessionId}/review`);
    return response.data;
  },
};

export const tikzAPI = {
  renderTikz: async (tikzCode: string): Promise<{
    success: boolean;
    svg_content?: string;
    error_message?: string;
    cached?: boolean;
  }> => {
    const response = await api.post('/tikz/render', {
      tikz_code: tikzCode
    });
    return response.data;
  },
};

export const adminAPI = {
  getUsers: async (page = 1, perPage = 50, search?: string) => {
    const params: any = { page, per_page: perPage };
    if (search) params.search = search;
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  getUser: async (userId: string) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  updateUser: async (userId: string, updates: {
    username?: string;
    email?: string;
    rating?: number;
    subscription_status?: string;
    is_admin?: boolean;
  }) => {
    const response = await api.put(`/admin/users/${userId}`, updates);
    return response.data;
  },

  makeUserAdmin: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/make-admin`);
    return response.data;
  },

  removeUserAdmin: async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/remove-admin`);
    return response.data;
  },

  getAdminStats: async () => {
    const response = await api.get('/admin/stats/overview');
    return response.data;
  },

  getHealth: async (): Promise<AdminHealthPayload> => {
    const response = await api.get<AdminHealthPayload>('/admin/health');
    return response.data;
  },

  getUserAttempts: async (userId: string, limit = 50) => {
    const response = await api.get(`/admin/users/${userId}/attempts`, { params: { limit } });
    return response.data;
  },
};

export const profileIconAPI = {
  getAvailableIcons: async () => {
    const response = await api.get('/profile-icons/available');
    return response.data;
  },

  selectIcon: async (iconId: string) => {
    const response = await api.put('/profile-icons/select', { icon_id: iconId });
    return response.data;
  },

  checkUnlocks: async () => {
    const response = await api.post('/profile-icons/check-unlocks');
    return response.data;
  },
};

export const leaderboardAPI = {
  getRatingLeaderboard: async (timePeriod: string = 'all_time') => {
    const response = await api.get('/leaderboard/rating', { params: { time_period: timePeriod } });
    return response.data;
  },

  getSurvivalLeaderboard: async (timePeriod: string = 'all_time') => {
    const response = await api.get('/leaderboard/survival', { params: { time_period: timePeriod } });
    return response.data;
  },

  getRushLeaderboard: async (durationMinutes: number, timePeriod: string = 'all_time') => {
    const response = await api.get('/leaderboard/rush', {
      params: { duration_minutes: durationMinutes, time_period: timePeriod }
    });
    return response.data;
  },
};

export const subscriptionAPI = {
  getStatus: async (): Promise<SubscriptionStatus> => {
    const response = await api.get('/subscriptions/status');
    return response.data;
  },
};

export default api;
